<template>
  <div class="stock-record-container">
    <el-card class="page-card">
      <div slot="header" class="card-header">
        <h2>库存记录管理</h2>
        <div>
          <el-button type="primary" @click="handleAddStockRecord">添加库存记录</el-button>
          <el-button type="success" @click="handleViewStock">库存</el-button>
        </div>
      </div>
      
      <!-- 数据表格 -->
      <el-table 
        v-loading="loading" 
        :data="stockRecords" 
        border 
        style="width: 100%; margin-top: 20px;"
        @selection-change="handleSelectionChange"
      >
        <!-- 表格列定义保持不变 -->
        <el-table-column label="记录ID" width="80">
          <template slot-scope="scope">
            {{ scope.$index + 1 }}
          </template>
        </el-table-column>
        <el-table-column prop="productName" label="产品名称" width="120"></el-table-column>
        <el-table-column prop="quantity" label="数量" width="80"></el-table-column>
        <el-table-column prop="productUnit" label="单位" width="80"></el-table-column>
        <el-table-column prop="groundName" label="场地" width="100"></el-table-column>
        <el-table-column prop="carNumber" label="车牌号" width="100"></el-table-column>
        <el-table-column prop="recordDate" label="记录日期" width="120"></el-table-column>
        <el-table-column prop="content" label="备注"></el-table-column>
      </el-table>
    </el-card>
    
    <!-- 查看详情对话框 -->
    <el-dialog
      title="库存记录详情"
      :visible.sync="detailDialogVisible"
      width="50%"
    >
      <el-descriptions column="1" border>
        <el-descriptions-item label="记录ID">
          {{ stockRecords.findIndex(item => item.recordId === currentRecord.recordId) + 1 }}
        </el-descriptions-item>
        <el-descriptions-item label="产品名称">{{ currentRecord.productName }}</el-descriptions-item>
        <el-descriptions-item label="数量">{{ currentRecord.quantity }} {{ currentRecord.productUnit }}</el-descriptions-item>
        <el-descriptions-item label="场地">{{ currentRecord.groundName }}</el-descriptions-item>
        <el-descriptions-item label="车牌号">{{ currentRecord.carNumber || '无' }}</el-descriptions-item>
        <el-descriptions-item label="记录日期">{{ currentRecord.recordDate }}</el-descriptions-item>
        <el-descriptions-item label="备注">{{ currentRecord.content || '无' }}</el-descriptions-item>
      </el-descriptions>
      <span slot="footer" class="dialog-footer">
        <el-button @click="detailDialogVisible = false">关闭</el-button>
      </span>
    </el-dialog>
    
    <!-- 库存表单对话框 -->
    <el-dialog
      title="库存信息"
      :visible.sync="stockDialogVisible"
      width="30%"
      :before-close="handleCloseStockDialog"
    >
      <el-table 
        v-loading="stockLoading" 
        :data="stockData" 
        border 
        style="width: 100%;"
      >
        <!-- 库存表格列定义保持不变 -->
        <el-table-column label="库存ID" width="80">
          <template slot-scope="scope">
            {{ scope.$index + 1 }}
          </template>
        </el-table-column>
        <el-table-column prop="productName" label="产品名称" width="150"></el-table-column>
        <el-table-column prop="stock" label="库存数量" width="100"></el-table-column>
        <el-table-column prop="productUnit" label="单位" width="80"></el-table-column>
        <el-table-column prop="groundName" label="场地" width="120"></el-table-column>
      </el-table>
      <span slot="footer" class="dialog-footer">
        <el-button type="primary" @click="handleAddStockItem">添加库存货物</el-button>
        <el-button @click="stockDialogVisible = false">关闭</el-button>
      </span>
    </el-dialog>
    
    <!-- 添加库存货物对话框 -->
    <el-dialog
      title="添加库存货物"
      :visible.sync="addStockDialogVisible"
      width="20%"
      :before-close="handleCloseAddStockDialog"
    >
      <!-- 表单内容保持不变 -->
      <el-form :model="addStockForm" ref="addStockForm" label-width="100px" class="add-stock-form">
        <el-form-item label="产品分类" prop="categoryId" :rules="[{ required: true, message: '请选择产品分类', trigger: 'change' }]">
          <el-select 
            v-model="addStockForm.categoryId" 
            placeholder="请选择产品分类"
            clearable
            @change="handleCategoryChange"
          >
            <el-option
              v-for="category in categories"
              :key="category.categoryId"
              :label="category.categoryName"
              :value="category.categoryId"
            ></el-option>
          </el-select>
        </el-form-item>
        
        <el-form-item label="产品名称" prop="productId" :rules="[{ required: true, message: '请选择产品', trigger: 'change' }]">
          <el-select 
            v-model="addStockForm.productId" 
            placeholder="请先选择产品分类"
            clearable
            :disabled="!addStockForm.categoryId"
            @change="handleProductChange"
          >
            <el-option
              v-for="product in products"
              :key="product.productId"
              :label="`${product.productName} (${product.productUnit})`"
              :value="product.productId"
            ></el-option>
          </el-select>
        </el-form-item>
        
        <el-form-item label="存放场地" prop="groundId" :rules="[{ required: true, message: '请选择场地', trigger: 'change' }]">
          <el-select 
            v-model="addStockForm.groundId" 
            placeholder="请选择场地"
            clearable
          >
            <el-option
              v-for="ground in grounds"
              :key="ground.groundId"
              :label="ground.groundName"
              :value="ground.groundId"
            ></el-option>
          </el-select>
        </el-form-item>
      </el-form>
      
      <span slot="footer" class="dialog-footer">
        <el-button @click="handleCloseAddStockDialog">取消</el-button>
        <el-button type="primary" @click="submitAddStock">确认添加</el-button>
      </span>
    </el-dialog>
    
    <!-- 添加库存记录对话框 -->
    <el-dialog
      title="添加库存记录"
      :visible.sync="addRecordDialogVisible"
      width="60%"
      :before-close="handleCloseAddRecordDialog"
    >
      <el-form :model="addRecordForm" ref="addRecordForm" label-width="120px" class="add-record-form">
        <!-- 场地选择器 -->
        <el-form-item label="选择场地" prop="groundId" :rules="[{ required: true, message: '请选择场地', trigger: 'change' }]">
          <el-select 
            v-model="addRecordForm.groundId" 
            placeholder="请选择场地"
            clearable
            @change="handleRecordGroundChange"
          >
            <el-option
              v-for="ground in stockGrounds"
              :key="ground.groundId"
              :label="ground.groundName"
              :value="ground.groundId"
            ></el-option>
          </el-select>
        </el-form-item>
        
        <!-- 产品选择器 -->
        <el-form-item label="选择产品" prop="productId" :rules="[{ required: true, message: '请选择产品', trigger: 'change' }]">
          <el-select 
            v-model="addRecordForm.productId" 
            placeholder="请先选择场地"
            clearable
            :disabled="addRecordForm.groundId === undefined || addRecordForm.groundId === null"
            @change="handleRecordProductChange"
          >
            <el-option
              v-for="product in stockProducts"
              :key="product.productId"
              :label="`${product.productName} (${product.productUnit})`"
              :value="product.productId"
            ></el-option>
          </el-select>
        </el-form-item>
        
        <!-- 日期选择器 -->
        <el-form-item label="记录日期" prop="recordDate" :rules="[{ required: true, message: '请选择日期', trigger: 'change' }]">
          <el-date-picker
            v-model="addRecordForm.recordDate"
            type="date"
            placeholder="选择日期"
            value-format="yyyy-MM-dd"
            style="width: 100%;"
          ></el-date-picker>
        </el-form-item>
        
        <!-- 数量输入框 -->
        <el-form-item label="数量" prop="quantity" :rules="[
          { required: true, message: '请输入数量', trigger: 'blur' },
          { type: 'number', min: 1, message: '数量必须为正整数', trigger: 'blur' }
        ]">
          <el-input
            v-model.number="addRecordForm.quantity"
            placeholder="请输入数量"
            type="number"
            min="1"
          ></el-input>
        </el-form-item>
        
        <!-- 司机选择器（显示车牌号，实际提交driverId） -->
        <el-form-item label="司机（车牌号）" prop="driverId">
          <el-select 
            v-model="addRecordForm.driverId" 
            placeholder="请选择司机（非必填）"
            clearable
            v-loading="driverLoading"
          >
            <el-option
              v-for="driver in drivers"
              :key="driver.userId"
              :label="driver.carNumber"
              :value="driver.userId"
            ></el-option>
          </el-select>
        </el-form-item>
        
        <!-- 备注 -->
        <el-form-item label="备注" prop="content">
          <el-input
            v-model="addRecordForm.content"
            placeholder="请输入备注信息"
            type="textarea"
            rows="3"
          ></el-input>
        </el-form-item>
      </el-form>
      
      <span slot="footer" class="dialog-footer">
        <el-button @click="handleCloseAddRecordDialog">取消</el-button>
        <el-button type="primary" @click="submitAddRecord">确认添加</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import adminrequest from '@/utils/adminrequest'
import apirequest from '@/utils/apirequest'

export default {
  name: 'StockRecordPage',
  data() {
    return {
      // 表格数据
      stockRecords: [],
      loading: false,
      
      // 选中的记录
      selectedRecords: [],
      
      // 详情对话框
      detailDialogVisible: false,
      currentRecord: {},
      
      // 库存对话框相关
      stockDialogVisible: false,
      stockData: [],
      stockLoading: false,
      
      // 添加库存货物对话框相关
      addStockDialogVisible: false,
      addStockForm: {
        categoryId: '',    // 产品分类ID
        productId: '',     // 产品ID
        groundId: ''       // 场地ID
      },
      
      // 选择器数据
      categories: [],    // 产品分类列表
      products: [],      // 产品列表
      grounds: [],       // 场地列表
      
      // 添加库存记录对话框相关
      addRecordDialogVisible: false,
      addRecordForm: {
        groundId: '',      // 场地ID
        productId: '',     // 产品ID
        recordDate: '',    // 记录日期
        quantity: null,    // 数量
        driverId: null,    // 司机ID（数字类型，替代carNumber）
        content: ''        // 备注
      },
      
      // 库存相关的场地和产品数据
      stockGrounds: [],   // 有库存的场地列表
      stockProducts: [],  // 特定场地的产品列表
      
      // 司机相关数据（包含userId和carNumber）
      drivers: [],        // 司机列表
      driverLoading: false, // 司机数据加载状态
      
      // 加载状态实例引用
      productLoadingInstance: null,
      recordProductLoadingInstance: null
    };
  },
  created() {
    // 页面加载时获取数据
    this.fetchStockRecords();
  },
  methods: {
    // 添加库存记录按钮点击事件
    handleAddStockRecord() {
      this.addRecordDialogVisible = true;
      this.fetchStockGrounds(); // 获取有库存的场地列表
      this.fetchDrivers();      // 获取司机列表（包含userId和carNumber）
      // 重置表单
      this.$refs.addRecordForm && this.$refs.addRecordForm.resetFields();
      this.stockProducts = [];
    },
    
    // 获取司机列表（包含userId和carNumber）
    fetchDrivers() {
      this.driverLoading = true;
      const options = {
        method: 'GET',
        url: '/admin/export/getCarNumber'
      };
      
      adminrequest.request(options)
        .then(response => {
          this.driverLoading = false;
          if (response.data.code === 200) {
            // 接口返回数据直接作为司机列表，包含userId和carNumber
            this.drivers = response.data.data;
          } else {
            this.$message.error(`获取司机信息失败: ${response.data.message}`);
          }
        })
        .catch(error => {
          this.driverLoading = false;
          console.error('获取司机信息失败:', error);
          this.$message.error('获取司机信息失败，请稍后重试');
        });
    },
    
    // 获取有库存的场地列表
    fetchStockGrounds() {
      const options = {
        method: 'GET',
        url: '/admin/stock/getStockGround'
      };
      
      adminrequest.request(options)
        .then(response => {
          if (response.data.code === 200) {
            this.stockGrounds = response.data.data;
          } else {
            this.$message.error(`获取场地列表失败: ${response.data.message}`);
          }
        })
        .catch(error => {
          console.error('获取场地列表失败:', error);
          this.$message.error('获取场地列表失败，请稍后重试');
        });
    },
    
    // 场地选择变化时，获取对应产品列表（库存记录）
    handleRecordGroundChange(groundId) {
      // 清空之前选择的产品
      this.addRecordForm.productId = '';
      
      if (groundId !== undefined && groundId !== null) {
        // 显示加载状态并保存引用
        this.recordProductLoadingInstance = this.$loading({
          lock: true,
          text: '加载产品中...',
          spinner: 'el-icon-loading',
          background: 'rgba(0, 0, 0, 0.7)'
        });
        
        // 根据场地ID获取产品列表
        const options = {
          method: 'GET',
          url: '/admin/stock/getStockProduct',
          params: { groundId: groundId }
        };
        
        adminrequest.request(options)
          .then(response => {
            // 关闭加载状态
            if (this.recordProductLoadingInstance) {
              this.recordProductLoadingInstance.close();
              this.recordProductLoadingInstance = null;
            }
            
            if (response.data.code === 200) {
              this.stockProducts = response.data.data;
              
              if (this.stockProducts.length === 0) {
                this.$message.warning('该场地没有库存产品数据');
              }
            } else {
              this.$message.error(`获取产品列表失败: ${response.data.message}`);
            }
          })
          .catch(error => {
            // 关闭加载状态
            if (this.recordProductLoadingInstance) {
              this.recordProductLoadingInstance.close();
              this.recordProductLoadingInstance = null;
            }
            
            console.error('获取产品列表失败:', error);
            this.$message.error('获取产品列表失败，请稍后重试');
          });
      } else {
        this.stockProducts = [];
      }
    },
    
    // 产品选择变化时的处理（库存记录）
    handleRecordProductChange(productId) {
      if (productId) {
        const selectedProduct = this.stockProducts.find(p => p.productId === productId);
        if (selectedProduct) {
          console.log(`已选择产品: ${selectedProduct.productName}, 单位: ${selectedProduct.productUnit}`);
        }
      }
    },
    
    // 提交添加库存记录
    submitAddRecord() {
      this.$refs.addRecordForm.validate(valid => {
        if (valid) {
          // 显示加载状态
          const loading = this.$loading({
            lock: true,
            text: '正在添加库存记录...',
            spinner: 'el-icon-loading',
            background: 'rgba(0, 0, 0, 0.7)'
          });
          
          // 表单验证通过，提交数据（包含driverId）
          const options = {
            method: 'POST',
            url: '/admin/stock/addRecord',
            data: this.addRecordForm
          };
          
          adminrequest.request(options)
            .then(response => {
              loading.close();
              if (response.data.code === 200) {
                this.$message.success('添加库存记录成功');
                this.handleCloseAddRecordDialog();
                // 刷新库存记录列表
                this.fetchStockRecords();
              } else {
                this.$message.error(`添加失败: ${response.data.message || '未知错误'}`);
              }
            })
            .catch(error => {
              loading.close();
              console.error('添加库存记录失败:', error);
              this.$message.error('添加失败，请稍后重试');
            });
        } else {
          // 表单验证失败
          return false;
        }
      });
    },
    
    // 关闭添加库存记录对话框
    handleCloseAddRecordDialog() {
      // 关闭任何可能存在的加载状态
      if (this.recordProductLoadingInstance) {
        this.recordProductLoadingInstance.close();
        this.recordProductLoadingInstance = null;
      }
      
      this.addRecordDialogVisible = false;
      this.$refs.addRecordForm && this.$refs.addRecordForm.resetFields();
      this.stockProducts = []; // 清空产品列表
    },
    
    // 库存按钮点击事件
    handleViewStock() {
      this.stockDialogVisible = true;
      this.fetchStockData();
    },
    
    // 添加库存货物按钮点击事件
    handleAddStockItem() {
      // 打开添加库存货物对话框前，先加载分类和场地数据
      this.addStockDialogVisible = true;
      this.fetchCategories();
      this.fetchGrounds();
      
      // 重置产品列表和表单
      this.products = [];
      this.$refs.addStockForm && this.$refs.addStockForm.resetFields();
    },
    
    // 获取产品分类列表
    fetchCategories() {
      const options = {
        method: 'GET',
        url: '/api/select/getCategory'
      };
      
      apirequest.request(options).then(response => {
        if (response.data.code === 200) {
          this.categories = response.data.data;
        } else {
          this.$message.error('获取产品分类失败：' + response.data.message);
        }
      }).catch(error => {
        console.error(error);
        this.$message.error('网络请求错误：' + error.message);
      });
    },
    
    // 获取场地列表
    fetchGrounds() {
      return apirequest.get('/api/select/getGround')
        .then(response => {
          if (response.data.code === 200) {
            this.grounds = response.data.data;
          } else {
            this.$message.error(`获取场地列表失败: ${response.data.message}`);
          }
        })
        .catch(error => {
          console.error('获取场地列表失败:', error);
          this.$message.error('获取场地列表失败，请稍后重试');
        });
    },
    
    // 分类选择变化时，获取对应产品列表
    handleCategoryChange(categoryId) {
      // 清空之前选择的产品
      this.addStockForm.productId = '';
      
      if (categoryId) {
        // 显示加载状态并保存引用
        this.productLoadingInstance = this.$loading({
          lock: true,
          text: '加载产品中...',
          spinner: 'el-icon-loading',
          background: 'rgba(0, 0, 0, 0.7)'
        });
        
        // 根据分类ID获取产品列表
        apirequest.get(`/api/select/getProduct?categoryId=${categoryId}`)
          .then(response => {
            // 关闭加载状态
            if (this.productLoadingInstance) {
              this.productLoadingInstance.close();
              this.productLoadingInstance = null;
            }
            
            if (response.data.code === 200) {
              this.products = response.data.data;
              
              // 如果没有产品数据，给出提示
              if (this.products.length === 0) {
                this.$message.warning('该分类下没有产品数据');
              }
            } else {
              this.$message.error(`获取产品列表失败: ${response.data.message}`);
            }
          })
          .catch(error => {
            // 关闭加载状态
            if (this.productLoadingInstance) {
              this.productLoadingInstance.close();
              this.productLoadingInstance = null;
            }
            
            console.error('获取产品列表失败:', error);
            this.$message.error('获取产品列表失败，请稍后重试');
          });
      } else {
        // 如果分类ID为空，清空产品列表
        this.products = [];
      }
    },
    
    // 产品选择变化时的处理
    handleProductChange(productId) {
      if (productId) {
        const selectedProduct = this.products.find(p => p.productId === productId);
        if (selectedProduct) {
          console.log(`已选择产品: ${selectedProduct.productName}, 单位: ${selectedProduct.productUnit}`);
        }
      }
    },
    
    // 提交添加库存货物
submitAddStock() {
  this.$refs.addStockForm.validate(valid => {
    if (valid) {
      // 创建FormData对象
      const form = new FormData();
      form.append("groundId", this.addStockForm.groundId);
      form.append("productId", this.addStockForm.productId);
      
      // 显示加载状态
      const loading = this.$loading({
        lock: true,
        text: '正在添加库存...',
        spinner: 'el-icon-loading',
        background: 'rgba(0, 0, 0, 0.7)'
      });
      
      // 获取FormData的边界值(boundary)
      const formDataBoundary = form._boundary || '---011000010111000001101001';
      
      // 表单验证通过，提交数据
      const options = {
        method: 'POST',
        // 使用完整URL，与示例保持一致
        url: '/admin/stock/addStock',
        headers: {
          'content-type': `multipart/form-data; boundary=${formDataBoundary}`
        },
        data: form  // 直接使用FormData对象，而不是字符串'[form]'
      };
      
      // 直接使用axios而不是封装的adminrequest，与示例保持一致
      adminrequest.request(options)
        .then(function (response) {
          loading.close();
          console.log(response.data);
          
          if (response.data && response.data.code === 200) {
            this.$message.success('添加库存货物成功');
            this.handleCloseAddStockDialog();
            this.fetchStockData();
            this.fetchStockRecords();
          } else {
            this.$message.error(`添加失败: ${response.data?.message || '未知错误'}`);
          }
        }.bind(this))  // 绑定this上下文
        .catch(function (error) {
          loading.close();
          console.error(error);
          
          if (error.response) {
            this.$message.error(`添加失败: ${error.response.data?.message || '服务器错误'}`);
          } else if (error.request) {
            this.$message.error('添加失败: 未收到服务器响应');
          } else {
            this.$message.error(`添加失败: ${error.message}`);
          }
        }.bind(this));  // 绑定this上下文
    } else {
      // 表单验证失败
      return false;
    }
  });
}
,
    
    // 关闭添加库存货物对话框
    handleCloseAddStockDialog() {
      // 关闭任何可能存在的加载状态
      if (this.productLoadingInstance) {
        this.productLoadingInstance.close();
        this.productLoadingInstance = null;
      }
      
      this.addStockDialogVisible = false;
      this.$refs.addStockForm && this.$refs.addStockForm.resetFields();
      this.products = []; // 清空产品列表
    },
    
    // 获取库存数据
    fetchStockData() {
      this.stockLoading = true;
      const options = {
        method: 'GET',
        url: '/admin/stock/getStock'
      };
      
      adminrequest.request(options)
        .then(response => {
          this.stockLoading = false;
          if (response.data.code === 200) {
            this.stockData = response.data.data;
          } else {
            this.$message.error(`获取库存数据失败: ${response.data.message}`);
          }
        })
        .catch(error => {
          this.stockLoading = false;
          console.error('请求库存数据失败:', error);
          this.$message.error('获取库存数据失败，请稍后重试');
        });
    },
    
    // 关闭库存对话框时的处理
    handleCloseStockDialog() {
      this.stockDialogVisible = false;
      this.stockData = [];
    },
    
    // 获取库存记录数据
    fetchStockRecords() {
      this.loading = true;
      const options = {
        method: 'GET',
        url: '/admin/stock/getRecord'
      };
      
      adminrequest.request(options)
        .then(response => {
          this.loading = false;
          if (response.data.code === 200) {
            this.stockRecords = response.data.data;
          } else {
            this.$message.error(`获取数据失败: ${response.data.message}`);
          }
        })
        .catch(error => {
          this.loading = false;
          console.error('请求失败:', error);
          this.$message.error('获取数据失败，请稍后重试');
        });
    },
    
    // 处理表格选择变化
    handleSelectionChange(selection) {
      this.selectedRecords = selection;
    },
    
    // 查看记录详情
    handleView(record) {
      this.currentRecord = { ...record };
      this.detailDialogVisible = true;
    },
    
    // 编辑记录
    handleEdit(record) {
      this.$message.info(`编辑记录 ${this.stockRecords.findIndex(item => item.recordId === record.recordId) + 1}`);
    }
  }
};
</script>

<style scoped>
.stock-record-container {
  padding: 20px;
  background-color: #f5f7fa;
  min-height: 100vh;
}

.page-card {
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.add-stock-form, .add-record-form {
  margin-top: 20px;
}

.el-form-item {
  margin-bottom: 15px;
}
</style>
    